// src/components/Login.js

import React, { useState, useRef, useEffect } from 'react';
import './Login.css';
import { useNavigate } from 'react-router-dom';
import {
  validateMobileNumber,
  validateOTP,
  validateTOTP,
  completeAuthentication,
  getTOTPQRCode,
  isTOTPSetupComplete,
  resetTOTPSetup
} from '../../services/authService';
import QRCode from 'qrcode';

const Login = () => {
  const [authStep, setAuthStep] = useState(1); // 1: Mobile, 2: OTP, 3: TOTP
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showQR, setShowQR] = useState(false);
  const navigate = useNavigate();

  // Refs for OTP inputs
  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  // Generate QR code when entering TOTP step
  useEffect(() => {
    if (authStep === 3) {
      const setupComplete = isTOTPSetupComplete();
      setShowQR(!setupComplete);

      if (!setupComplete) {
        const otpauthUrl = getTOTPQRCode();
        QRCode.toDataURL(otpauthUrl)
          .then(url => setQrCodeUrl(url))
          .catch(err => console.error('QR Code generation error:', err));
      }
    }
  }, [authStep]);

  // Step 1: Mobile Number Validation
  const handleMobileSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = validateMobileNumber(mobile);

    if (result.success) {
      setAuthStep(2);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Step 2: OTP Input Handlers
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleOtpSubmit(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split('').forEach((digit, i) => {
      if (i < 6) newOtp[i] = digit;
    });
    setOtp(newOtp);

    // Focus last filled input or first empty
    const focusIndex = Math.min(pastedData.length, 5);
    otpRefs[focusIndex].current?.focus();

    // Auto-submit if complete
    if (pastedData.length === 6) {
      handleOtpSubmit(pastedData);
    }
  };

  const handleOtpSubmit = (otpValue) => {
    setError('');
    setLoading(true);

    const result = validateOTP(otpValue);

    if (result.success) {
      setAuthStep(3);
    } else {
      setError(result.error);
      setOtp(['', '', '', '', '', '']);
      otpRefs[0].current?.focus();
    }

    setLoading(false);
  };

  // Step 3: TOTP Validation
  const handleTotpSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = validateTOTP(totpCode);

    if (result.success) {
      completeAuthentication();
      navigate('/admin');
    } else {
      setError(result.error);
      setTotpCode('');
    }

    setLoading(false);
  };

  // Back button handler
  const handleBack = () => {
    setError('');
    if (authStep === 2) {
      setAuthStep(1);
      setOtp(['', '', '', '', '', '']);
    } else if (authStep === 3) {
      setAuthStep(2);
      setTotpCode('');
    }
  };

  // Reset QR code (for re-setup)
  const handleResetQR = () => {
    resetTOTPSetup();
    setShowQR(true);
    const otpauthUrl = getTOTPQRCode();
    QRCode.toDataURL(otpauthUrl)
      .then(url => setQrCodeUrl(url))
      .catch(err => console.error('QR Code generation error:', err));
  };

  return (
    <div className="login-container">
      <div className="login-form-section">
        <h1>Blog Login</h1>

        {/* Progress Indicator */}
        <div className="progress-indicator">
          Step {authStep} of 3
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Step 1: Mobile Number */}
        {authStep === 1 && (
          <form onSubmit={handleMobileSubmit}>
            <div className="form-group">
              <label htmlFor="mobile">Mobile Number</label>
              <input
                type="tel"
                id="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+91-9910952142"
                required
                disabled={loading}
                autoFocus
              />
              <div className="input-hint">Enter your authorized mobile number</div>
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </form>
        )}

        {/* Step 2: OTP */}
        {authStep === 2 && (
          <div>
            <div className="step-heading">
              <h2>Enter OTP</h2>
              <p>Enter the 6-digit OTP</p>
            </div>
            <div className="otp-container" onPaste={handleOtpPaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={otpRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="otp-digit"
                  disabled={loading}
                />
              ))}
            </div>
            <button onClick={handleBack} className="back-button" disabled={loading}>
              Back
            </button>
          </div>
        )}

        {/* Step 3: TOTP Authenticator */}
        {authStep === 3 && (
          <div>
            <div className="step-heading">
              <h2>Authenticator Code</h2>
              <p>Enter code from your authenticator app</p>
            </div>

            {showQR && qrCodeUrl && (
              <div className="qr-code-container">
                <p className="qr-instruction">Scan this QR code with Google Authenticator or similar app:</p>
                <img src={qrCodeUrl} alt="TOTP QR Code" className="qr-code" />
              </div>
            )}

            {!showQR && (
              <div className="qr-reset-container">
                <p>Already set up authenticator app</p>
                <button onClick={handleResetQR} className="reset-qr-button" type="button">
                  Show QR Code Again
                </button>
              </div>
            )}

            <form onSubmit={handleTotpSubmit}>
              <div className="form-group">
                <label htmlFor="totpCode">6-Digit Code</label>
                <input
                  type="text"
                  id="totpCode"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  required
                  disabled={loading}
                  autoFocus
                  className="totp-input"
                />
              </div>
              <button type="submit" className="login-button" disabled={loading || totpCode.length !== 6}>
                {loading ? 'Verifying...' : 'Log In'}
              </button>
            </form>

            <button onClick={handleBack} className="back-button" disabled={loading}>
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
